import React, { useState, useEffect } from "react";
import {
  Container,
  Tab,
  Tabs,
  Form,
  Button,
  Table,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import "./UserPanel.css";
import axios from "./axiosConfig";

type Organization = {
  id: number;
  name: string;
};

type User = {
  id: number;
  login: string;
  email: string;
  name: string;
  lastActive?: string;
  organizations: Organization[];
  isGroupMailbox?: boolean;
};

type UserData = {
  id?: number;
  login: string;
  email: string;
  name: string;
  organizations: Organization[];
  isGroupMailbox?: boolean;
};

type FormControlElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

function UserPanel() {
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData>({
    login: "",
    name: "",
    email: "",
    organizations: [],
    isGroupMailbox: false,
  });

  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingOrigins, setLoadingOrigins] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newOrgName, setNewOrgName] = useState("");
  const [showOrgModal, setShowOrgModal] = useState(false);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const normalizeText = (v: unknown) =>
    typeof v === "string" ? v.trim() : "";

  const isReservedLogin = (login: string) => {
    const s = login.trim().toLowerCase();
    return s === "null" || s === "undefined";
  };

  const validateUserData = (data: UserData) => {
    const errors: Record<string, string> = {};

    const login = normalizeText(data.login);
    const email = normalizeText(data.email);
    const name = normalizeText(data.name);

    if (!data.organizations || data.organizations.length === 0) {
      errors.organizations = "Please select at least one organization.";
    }

    if (!data.isGroupMailbox) {
      if (!login) errors.login = "Login is required.";
      else if (isReservedLogin(login))
        errors.login = "Login cannot be 'null' or 'undefined'.";
    } else {
      // Group mailbox: require email, and login can be auto-generated by email
      if (!email) errors.email = "Email is required for group mailbox.";
      // If user typed a login anyway, still validate it
      if (login && isReservedLogin(login)) {
        errors.login = "Login cannot be 'null' or 'undefined'.";
      }
    }

    if (!email) errors.email = "Email is required.";
    if (!name) errors.name = "Name is required.";

    return {
      ok: Object.keys(errors).length === 0,
      errors,
      normalized: { login, email, name },
    };
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await axios.get("/api/organizations");
      setOrganizations(response.data);
      setLoadingOrgs(false);
    } catch (err: any) {
      setError(err?.message || "Failed to load organizations");
      setLoadingOrgs(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/api/users");
      const data = response.data;
      console.log("Fetched users:", data);
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.error("Fetched data is not an array:", data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleOpenModal = (user: User | null = null) => {
    setFormErrors({});
    if (user) {
      setIsEditing(true);
      setSelectedUser(user);
      setUserData({
        id: user.id,
        login: user.login ?? "",
        name: user.name ?? "",
        email: user.email ?? "",
        organizations: user.organizations ?? [],
        isGroupMailbox: user.isGroupMailbox ?? false,
      });
    } else {
      setIsEditing(false);
      setSelectedUser(null);
      setUserData({
        login: "",
        name: "",
        email: "",
        organizations: [],
        isGroupMailbox: false,
      });
    }
    setShowModal(true);
  };

  const handleSaveOrganization = async () => {
    if (!newOrgName.trim()) {
      console.error("Organization name cannot be empty");
      return;
    }
    try {
      const response = await axios.post("/api/organizations", {
        name: newOrgName.trim(),
      });
      if (response.status === 200) {
        fetchOrganizations();
        setShowOrgModal(false);
        setNewOrgName("");
      } else {
        console.error("Failed to add organization", response.data);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingField(null);
    setFormErrors({});
  };

  const handleFormChange = (e: React.ChangeEvent<FormControlElement>) => {
    const { id, value } = e.target;
    setUserData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveUser = async () => {
    const { ok, errors, normalized } = validateUserData(userData);
    setFormErrors(errors);
    if (!ok) return;

    let finalLogin = normalized.login;

    if (userData.isGroupMailbox) {
      finalLogin = finalLogin || normalized.email; // use email as login if empty
      if (!finalLogin) {
        setFormErrors((prev) => ({
          ...prev,
          login: "Login could not be generated.",
        }));
        return;
      }
      if (isReservedLogin(finalLogin)) {
        setFormErrors((prev) => ({
          ...prev,
          login: "Auto login is invalid.",
        }));
        return;
      }
    }

    const preparedUserData = {
      ...userData,
      login: finalLogin,
      email: normalized.email,
      name: normalized.name,
      organizationIds: userData.organizations.map((org) => org.id),
    };

    const method = isEditing ? "put" : "post";
    const url = isEditing ? `/api/users/${userData.id}` : "/api/users";

    try {
      const response = await (axios as any)[method](url, preparedUserData);
      if (response.status === 200) {
        fetchUsers();
        handleCloseModal();
      } else {
        console.error("Failed to process user", response.data);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteUser = async () => {
    if (selectedUser) {
      try {
        const response = await axios.delete(`/api/users/${selectedUser.id}`);
        if (response.status === 200) {
          fetchUsers();
          handleCloseModal();
        } else {
          console.error("Failed to delete user");
        }
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };

  const filteredUsers = users.filter((user) => {
    const login = (user.login || "").toLowerCase();
    const email = (user.email || "").toLowerCase();
    const name = (user.name || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return login.includes(query) || email.includes(query) || name.includes(query);
  });

  // Optional: keep these handlers (not used in UI now)
  const handleEditOrganization = async (org: { id: any; name?: string }) => {
    try {
      const response = await axios.put(`/api/organizations/${org.id}`, org);
      if (response.status === 200) {
        fetchOrganizations();
        console.log("Organization updated successfully");
      } else {
        console.error("Failed to update organization");
      }
    } catch (error) {
      console.error("Error updating organization:", error);
    }
  };

  const handleDeleteOrganization = async (orgId: number) => {
    try {
      const response = await axios.delete(`/api/organizations/${orgId}`);
      if (response.status === 200) {
        fetchOrganizations();
        console.log("Organization deleted successfully");
      } else {
        console.error("Failed to delete organization");
      }
    } catch (error) {
      console.error("Error deleting organization:", error);
    }
  };

  return (
    <Container>
      <Tabs
        defaultActiveKey="allUsers"
        id="uncontrolled-tab-example"
        className="mb-3 my-dark-tabs"
      >
        <Tab eventKey="allUsers" title="All users">
          <Form className="d-flex mb-3">
            <Button
              variant="outline-success"
              onClick={() => handleOpenModal()}
              style={{ marginLeft: "auto" }}
            >
              New User
            </Button>
          </Form>

          <Table striped bordered hover size="sm" responsive variant="dark">
            <thead>
              <tr>
                <th>Login</th>
                <th>Email</th>
                <th>Name</th>
                <th>Organization</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.login}{" "}
                    {user.isGroupMailbox && (
                      <span className="badge bg-info ms-1">Group</span>
                    )}
                  </td>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td>{user.organizations.map((org) => org.name).join(", ")}</td>
                  <td>{user.lastActive || "N/A"}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleOpenModal(user)}
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="allOrganizations" title="All Organizations">
          <Form className="d-flex mb-3">
            <Button
              variant="outline-success"
              onClick={() => setShowOrgModal(true)}
              style={{ marginLeft: "auto" }}
            >
              New Organization
            </Button>
          </Form>

          {loadingOrgs ? (
            <p>Loading organizations...</p>
          ) : error ? (
            <p>Error loading organizations: {error}</p>
          ) : (
            <Table striped bordered hover size="sm" responsive variant="dark">
              <thead>
                <tr>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.id}>
                    <td>{org.name}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>
      </Tabs>

      <Modal show={showModal} onHide={handleCloseModal} centered className="dark-modal">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit User" : "Add New User"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {Object.keys(userData)
              .filter(
                (field) =>
                  field !== "id" &&
                  field !== "password" &&
                  field !== "organizations" &&
                  field !== "origins"
              )
              .map((field, index) => (
                <Form.Group as={Row} className="mb-3" key={index}>
                  <Form.Label column sm="3">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </Form.Label>
                  <Col sm="9">
                    <Form.Control
                      type="text"
                      id={field}
                      value={(userData[field as keyof UserData] as string) || ""}
                      onChange={(e) => {
                        setFormErrors((prev) => ({ ...prev, [field]: "" }));
                        handleFormChange(e);
                      }}
                      onBlur={() => {
                        const { errors } = validateUserData(userData);
                        setFormErrors(errors);
                      }}
                      disabled={field === "login" && !!userData.isGroupMailbox}
                      isInvalid={!!formErrors[field]}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors[field]}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              ))}

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm="3">
                Organizations
              </Form.Label>
              <Col sm="9">
                <Form.Select
                  id="organizations"
                  multiple
                  value={userData.organizations.map((org) => org.id.toString())}
                  onChange={(e) => {
                    setFormErrors((prev) => ({ ...prev, organizations: "" }));
                    const selectedOptions = Array.from(
                      e.target.selectedOptions,
                      (option) => ({
                        id: parseInt(option.value, 10),
                        name: option.label,
                      })
                    );
                    setUserData((prev) => ({
                      ...prev,
                      organizations: selectedOptions,
                    }));
                  }}
                  isInvalid={!!formErrors.organizations}
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Form.Select>

                {formErrors.organizations && (
                  <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                    {formErrors.organizations}
                  </div>
                )}
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm="3">
                User Type
              </Form.Label>
              <Col sm="9">
                <Form.Check
                  type="switch"
                  id="isGroupMailbox"
                  label="This is a group mailbox (no login, no password email)"
                  checked={!!userData.isGroupMailbox}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormErrors((prev) => ({ ...prev, login: "", email: "" }));
                    setUserData((prev) => {
                      const email = normalizeText(prev.email);
                      return {
                        ...prev,
                        isGroupMailbox: checked,
                        login: checked ? (email || prev.login) : prev.login,
                      };
                    });
                  }}
                />
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          {isEditing ? (
            <Button variant="danger" onClick={handleDeleteUser} className="me-2">
              Delete User
            </Button>
          ) : null}
          <Button variant="primary" onClick={handleSaveUser}>
            {isEditing ? "Update User" : "Create User"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showOrgModal} onHide={() => setShowOrgModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Organization</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Organization Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter organization name"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOrgModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveOrganization}>
            Save Organization
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default UserPanel;
